import PropTypes from "prop-types";
import React, { useState, useCallback, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import debounce from "lodash/debounce";
import sanitizeHtml from "sanitize-html";
import Loading from "../components/Loading";
import StampError from "../components/StampError";
import StampCreated from "../components/StampCreated";
import StampContentId from "../components/StampContentId";
import StampFromFile from "../components/StampFromFile";
import { processFile } from "../utils/processFile";
import { verifyHashWorker } from "../utils/verifyHashWorker";
import { pingForwarder } from "../utils/pingForwarder";
import TriggerHelp from "../components/TriggerHelp";
import NeedHelp from "../components/NeedHelp";
import Landing from "../components/landing-components/Index";
import LearnMore from "../components/landing-components/LearnMore";
import { useStylesButton } from "../utils/mantine/useStylesButton";

import {
  fetchAllCollectionsIsPinnedWithoutPagination,
  fetchQuotaData,
} from "../utils/apis";
import SelectPopUpCollection from "../components/SelectPopUpCollection";
import StampFromText from "../components/StampFromText";
import StampFromPortfolio from "../components/StampFromPortfolio";
import CustomButton from "../components/mantineCustomComponents/CustomButton";
import { Box, Flex, Text, Tooltip, useMantineTheme } from "@mantine/core";
import CustomTooltip from "../components/mantineCustomComponents/CustomTooltip";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import CustomAnchor from "../components/mantineCustomComponents/CustomAnchor";
import { formatHexStr } from "../utils/formatHexStr";
import { sha3_256_hash_bytes } from "../utils/cryptoUtils";
import {
  validatePortfolioContent,
  isPortfolioCollection,
  normalizePortfolioContent,
  cleanHtmlContent,
  getPortfolioTooltipText,
} from "../utils/portfolioValidation";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import { useStylesGeneral } from "../utils/mantine/stampTabContent/useStylesGeneral";
import { useStylesLoader } from "../utils/mantine/useStylesLoader";

const StampForm = ({ staticBasePath, djangoContext }) => {
  const PORTFOLIO_DISABLED = false;

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  //css classes
  const { classes: classesPage } = useStylesPage();

  const { classes: buttonClasses } = useStylesButton();

  const { classes: tooltipClasses } = useStylesTooltip();

  const { classes: GeneralClasses } = useStylesGeneral();

  const { classes: loaderClasses } = useStylesLoader();

  // Collection hash
  const [selectedOptionCollection, setSelectedOptionCollection] =
    useState(null);

  // Ref to access current collection in debounced functions
  const selectedOptionCollectionRef = useRef(selectedOptionCollection);
  useEffect(() => {
    selectedOptionCollectionRef.current = selectedOptionCollection;
  }, [selectedOptionCollection]);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleSelectedOptionCollection = useCallback((option) => {
    setSelectedOptionCollection(option);
  }, []);

  // set All Collection Names
  const [selectCollections, setSelectCollections] = useState(null);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleSelectCollections = useCallback((Collections) => {
    setSelectCollections(Collections);
    setActivateSelectCollection(Collections && Collections.length > 0);
  }, []);

  // fetch all Collection Names for select without pagination
  useEffect(() => {
    fetchAllCollectionsIsPinnedWithoutPagination(handleSelectCollections);
  }, []);

  // Validate content when collection changes
  useEffect(() => {
    if (
      !PORTFOLIO_DISABLED &&
      selectedOptionCollection &&
      isPortfolioCollection(selectedOptionCollection)
    ) {
      if (selectedFile && verificationMethod === "file") {
        validateFileForPortfolio(selectedFile);
      }
      if (text && verificationMethod === "text") {
        validateTextForPortfolio(text);
      }
    } else {
      // Clear validation if not a portfolio collection
      setFilePortfolioValidation({ isValid: true, message: "" });
      setTextPortfolioValidation({ isValid: true, message: "" });
    }
  }, [selectedOptionCollection, selectedFile, verificationMethod]);

  // Helper function to generate portfolio filename from collection
  const generatePortfolioFileName = useCallback((collection) => {
    const collectionName = collection?.name || "portfolio";
    // Sanitize collection name: remove special characters, replace spaces with underscores
    const sanitizedCollectionName = collectionName
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/\s+/g, "_")
      .toLowerCase();
    return `vBase-portfolio_${sanitizedCollectionName}.csv`;
  }, []);

  // Helper function to create portfolio file from normalized content
  const createPortfolioFile = useCallback(
    (normalizedContent, collection) => {
      const fileName = generatePortfolioFileName(collection);
      const blob = new Blob([normalizedContent], { type: "text/csv" });
      return new File([blob], fileName, { type: "text/csv" });
    },
    [generatePortfolioFileName],
  );

  // Update portfolio file name when collection changes (if normalized portfolio already exists)
  useEffect(() => {
    if (
      verificationMethod === "portfolio" &&
      normalizedPortfolio &&
      selectedOptionCollection
    ) {
      const file = createPortfolioFile(
        normalizedPortfolio,
        selectedOptionCollection,
      );
      setSelectedFile(file);
      handleProcessFile(file);
    }
  }, [
    selectedOptionCollection,
    normalizedPortfolio,
    verificationMethod,
    handleProcessFile,
    createPortfolioFile,
  ]);

  // Collection activate checkbox
  const [activateSelectCollection, setActivateSelectCollection] =
    useState(false);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleActivateSelectCollection = useCallback((isChecked) => {
    setActivateSelectCollection(isChecked);
  }, []);

  const [helpIsActive, setHelpIsActive] = useState(() => {
    const cookieValue = Cookies.get("helpIsActive");
    return cookieValue !== undefined ? JSON.parse(cookieValue) : true;
  });

  const handleHelpTriggerClick = () => {
    const newHelpIsActive = !helpIsActive;
    setHelpIsActive(newHelpIsActive);
    Cookies.set("helpIsActive", JSON.stringify(newHelpIsActive));
  };

  const [verificationMethod, setVerificationMethod] = useState(() => {
    const cookieVerificationMethod = Cookies.get("verificationMethod");

    let method;
    if (cookieVerificationMethod !== undefined) {
      method = JSON.parse(cookieVerificationMethod);
    }
    const allowedMethods = ["hash", "text", "portfolio"];

    // If portfolio disabled and method is portfolio, switch to file
    if (method === "portfolio" && PORTFOLIO_DISABLED) {
      Cookies.set("verificationMethod", JSON.stringify("file"));
      return "file";
    }

    return allowedMethods.includes(method) ? method : "file";
  });

  // Force activateSelectCollection when portfolio is loaded from cookie
  useEffect(() => {
    if (verificationMethod === "portfolio" && !activateSelectCollection) {
      setActivateSelectCollection(true);
    }
  }, [verificationMethod]);
  const [objectHash, setObjectHash] = useState(null);
  const [text, setText] = useState("");

  // Wrapper for setText with validation and verification
  const handleSetText = (newText) => {
    setText(newText);

    // Reset button state when text changes
    setButton(false);
    setCurrentScreen("");

    // Validate text for portfolio collections
    if (
      !PORTFOLIO_DISABLED &&
      selectedOptionCollection &&
      isPortfolioCollection(selectedOptionCollection) &&
      verificationMethod === "text"
    ) {
      validateTextForPortfolio(newText);
    }

    // Trigger verification with debounce for all text stamps
    if (newText && verificationMethod === "text") {
      handleDebouncedTextVerification(newText);
    } else if (!newText) {
      // Clear selected file if text is empty
      setSelectedFile(null);
    }
  };
  const [portfolioText, setPortfolioText] = useState("");
  const [portfolioValidation, setPortfolioValidation] = useState({
    isValid: false,
    message: "",
  });
  const [normalizedPortfolio, setNormalizedPortfolio] = useState("");

  // Wrapper for setPortfolioText with verification
  const handleSetPortfolioText = (newText) => {
    setPortfolioText(newText);

    // Reset button state when portfolio text changes
    setButton(false);
    setCurrentScreen("");

    // Trigger verification with debounce
    if (newText && verificationMethod === "portfolio") {
      handleDebouncedPortfolioVerification(newText);
    } else if (!newText) {
      // Clear selected file and normalized portfolio if text is empty
      setSelectedFile(null);
      setNormalizedPortfolio("");
    }
  };

  // Portfolio validation for File and Text when collection is portfolio type
  const [filePortfolioValidation, setFilePortfolioValidation] = useState({
    isValid: true,
    message: "",
  });

  const [textPortfolioValidation, setTextPortfolioValidation] = useState({
    isValid: true,
    message: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [stampData, setStampData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(""); // new state to manage screen
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    djangoContext.userIsAuthenticated,
  );
  const [button, setButton] = useState(false);

  // Check authentication dynamically on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { checkAuthentication } = await import("../utils/apis");
      await checkAuthentication(setIsAuthenticated);
    };
    checkAuth();
  }, []);
  const [isValid, setIsValid] = useState(true);

  const [fileName, setFileName] = useState("");

  const regex = /^(0x)?[0-9a-fA-F]{64}$/;
  let timeout = null;
  let timeoutDuration = 10 * 60 * 1000; //1 minute
  let lastTimeoutInvocation = Date.now();
  const onMouseMove = () => {
    if (!isAuthenticated) {
      return;
    }
    restartAutoReset();
  };
  const restartAutoReset = () => {
    // Check if the timeout has already occurred
    let isTimedOut =
      Date.now() > new Date(lastTimeoutInvocation + timeoutDuration);

    if (isTimedOut) {
      lastTimeoutInvocation = Date.now();
      pingForwarder(isAuthenticated);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        pingForwarder(isAuthenticated);
        clearTimeout(timeout);
      }, 0);
    }
  };

  const handleVerificationMethodChange = (method) => {
    if (method !== verificationMethod) {
      setCurrentScreen("");
      setObjectHash("");
      setSelectedFile(null);
      setIsValid(true);
      setButton(false);
      setText("");
      setPortfolioText("");
      setPortfolioValidation({ isValid: false, message: "" });
    }

    // Force activateSelectCollection to true for portfolio method
    if (method === "portfolio") {
      setActivateSelectCollection(true);
    }

    setVerificationMethod(method);
    Cookies.set("verificationMethod", JSON.stringify(method));
  };

  const handleDefaultScreen = () => {
    setButton(false);
    setError(false);
    setCurrentScreen("");
    setObjectHash("");
    setSelectedFile(null);
    setLoading(false);
    setText("");
    setPortfolioText("");
    setPortfolioValidation({ isValid: false, message: "" });
    setNormalizedPortfolio("");
  };

  const handleFileInput = async (event) => {
    event.preventDefault();
    if (isAuthenticated) {
      const file = event.target.files?.[0] || event.dataTransfer?.files?.[0];
      setButton(false);
      if (file) {
        setStampData(null);
        setObjectHash("");
        setSelectedFile(null);
        handleProcessFile(file);
        // Validate file for portfolio collections only if collection is selected and is portfolio
        if (
          !PORTFOLIO_DISABLED &&
          selectedOptionCollection &&
          isPortfolioCollection(selectedOptionCollection)
        ) {
          await validateFileForPortfolio(file);
        }
      }
    }
  };

  const handleTextToFile = () => {
    // Open the submit modal (text file already created via debounced verification)
    handleCreateSubmit();
  };

  const handleHashChange = (event) => {
    const value = event.target.value;
    if (isAuthenticated) {
      const isValid = regex.test(value);
      setIsValid(isValid || value === "");
      setObjectHash(value);
      setSelectedFile(null);
      setButton(false);
      if (!isLoading && value !== "" && isValid) {
        handleDebouncedUpdateHashWorker(value);
      }
      {
        setCurrentScreen("");
      }
    }
  };
  const handleDebouncedUpdateHashWorker = useCallback(
    debounce((value) => handleVerifyHashWorker(value), 2000),
    [handleVerifyHashWorker],
  );

  // Debounced verification for text input
  const handleDebouncedTextVerification = useCallback(
    debounce((textContent) => {
      // Skip verification if text is empty
      if (!textContent) {
        return;
      }

      // Validate content before verification (for portfolio collections)
      const currentCollection = selectedOptionCollectionRef.current;
      if (currentCollection && isPortfolioCollection(currentCollection)) {
        const cleanText = cleanHtmlContent(textContent);
        const validation = validatePortfolioContent(cleanText);

        // Skip verification if validation failed
        if (!validation.isValid) {
          return;
        }
      }

      // Create temporary file from text to calculate hash
      const encoder = new TextEncoder();
      const textByteArray = encoder.encode(textContent);
      const textHash = sha3_256_hash_bytes(textByteArray);
      const fileName = `vBase_text_${formatHexStr(
        textHash,
        6,
        0,
        1,
        true,
      )}.html`;

      const sanitizedHtml = sanitizeHtml(textContent, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(["img"]),
        allowedAttributes: {
          ...sanitizeHtml.defaults.allowedAttributes,
          img: ["src", "alt", "width", "height"],
          a: ["href", "target", "rel"],
        },
        allowedSchemes: ["data", "http", "https", "mailto"],
      });

      const htmlContent = `<!DOCTYPE html><html><head><title>Stamped file: ${formatHexStr(
        textHash,
        4,
        0,
        1,
        true,
      )}</title></head><body>${sanitizedHtml}</body></html>`;

      const blob = new Blob([htmlContent], { type: "text/html" });
      const file = new File([blob], fileName, { type: "text/html" });

      setSelectedFile(file);
      handleProcessFile(file);
    }, 2000),
    [],
  );

  // Debounced verification for portfolio input
  const handleDebouncedPortfolioVerification = useCallback(
    debounce((portfolioContent) => {
      // Skip verification if portfolio is empty
      if (!portfolioContent) {
        return;
      }

      // Validate portfolio content before verification
      const validation = validatePortfolioContent(portfolioContent);

      // Skip verification if portfolio validation failed
      if (!validation.isValid) {
        return;
      }

      // Normalize and create CSV file
      const normalized = normalizePortfolioContent(portfolioContent);
      setNormalizedPortfolio(normalized);

      // Create file with proper filename using collection name
      const collection = selectedOptionCollectionRef.current;
      const file = createPortfolioFile(normalized, collection);

      setSelectedFile(file);
      handleProcessFile(file);
    }, 2000),
    [createPortfolioFile, handleProcessFile],
  );
  const handleRemoveHash = () => {
    setCurrentScreen("");
    setObjectHash("");
    setSelectedFile(null);
    setButton(false);
    setIsValid(true);
  };
  const handleRemoveText = () => {
    setCurrentScreen("");
    setText("");
    setSelectedFile(null);
    setButton(false);
    setIsValid(true);
  };

  const handlePortfolioToFile = () => {
    // Portfolio file already created and verified through debounced verification
    // Just open the submit modal (called from mobile button click)
    handleCreateSubmit();
  };

  // Common validation function for portfolio content
  const validatePortfolioContentWrapper = (content, setValidation) => {
    if (
      PORTFOLIO_DISABLED ||
      !selectedOptionCollection ||
      !isPortfolioCollection(selectedOptionCollection)
    ) {
      setValidation({ isValid: true, message: "" });
      return;
    }

    // Clean HTML content for portfolio validation
    const cleanText = cleanHtmlContent(content);
    const validation = validatePortfolioContent(cleanText);
    setValidation(validation);
  };

  // Validate file content for portfolio collections
  const validateFileForPortfolio = async (file) => {
    if (
      PORTFOLIO_DISABLED ||
      !selectedOptionCollection ||
      !isPortfolioCollection(selectedOptionCollection)
    ) {
      setFilePortfolioValidation({ isValid: true, message: "" });
      return;
    }

    try {
      const content = await file.text();
      validatePortfolioContentWrapper(content, setFilePortfolioValidation);
    } catch {
      setFilePortfolioValidation({
        isValid: false,
        message: `Unable to read file "${file.name}" for portfolio validation. Please ensure the file is a valid text-based CSV file.`,
      });
    }
  };

  // Validate text content for portfolio collections
  const validateTextForPortfolio = (textContent) => {
    validatePortfolioContentWrapper(textContent, setTextPortfolioValidation);
  };

  const handleCreateSubmit = () => {
    // Show pop up confirmation create stamp
    setSelectPopUpShow(true);
  };

  // Add all Hashes supported
  const [allHashes, setAllHashes] = useState({});
  // Note: React setState functions (setIsLoading, setError, etc.) are guaranteed to be stable
  // between renders, so an empty dependency array is correct here
  const handleVerifyHashWorker = useCallback(
    (hash, supportedHashes, created = false) => {
      verifyHashWorker(
        hash,
        supportedHashes,
        created,
        setIsLoading,
        setError,
        setStampData,
        setCurrentScreen,
        setButton,
        setLoading,
      );
    },
    [], // Empty array is correct: all setState functions are stable
  );
  const handleProcessFile = useCallback(
    (file) => {
      // get hash with web3
      setSelectedFile(file);
      processFile(file, setObjectHash, handleVerifyHashWorker, setAllHashes);
    },
    [handleVerifyHashWorker],
  );

  // Common function to check if button should be disabled
  const isButtonDisabled = () => {
    // Disable button if user is not authenticated
    if (!isAuthenticated) {
      return true;
    }

    const collectionRequired =
      !selectedOptionCollection && activateSelectCollection;

    switch (verificationMethod) {
      case "text":
        // Text stamps need verification before enabling button
        return (
          !button ||
          !text ||
          !textPortfolioValidation.isValid ||
          collectionRequired
        );
      case "portfolio":
        // Portfolio stamps need verification before enabling button
        return (
          !button ||
          !portfolioText ||
          !portfolioValidation.isValid ||
          collectionRequired
        );
      case "file":
        // File stamps need portfolio validation if portfolio collection is selected
        return (
          !button ||
          (!PORTFOLIO_DISABLED &&
            selectedOptionCollection &&
            isPortfolioCollection(selectedOptionCollection) &&
            !filePortfolioValidation.isValid) ||
          collectionRequired
        );
      case "hash":
        // Content ID stamps don't need portfolio validation
        return !button || collectionRequired;
      default:
        return true;
    }
  };

  // Get tooltip text for portfolio stamps without collection (only for portfolio method)
  const portfolioTooltipText =
    verificationMethod === "portfolio"
      ? getPortfolioTooltipText(
          selectedOptionCollection,
          activateSelectCollection,
        )
      : null;

  const buttonStampClassName = `btn-stamp ${buttonClasses.buttonBlack} ${
    GeneralClasses.buttonStamp
  } ${isButtonDisabled() ? GeneralClasses.buttonFileDisabled : ""}`;

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 639);
  const isDisabled = !objectHash || (objectHash && currentScreen === "found");

  const handlePingForwarder = useCallback(() => {
    pingForwarder(isAuthenticated);
  }, [isAuthenticated]);
  useEffect(() => {
    handlePingForwarder();
    window.removeEventListener("mousemove", onMouseMove);
    window.addEventListener("mousemove", onMouseMove);

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 639);
    };
    restartAutoReset(0);
    // cleanup
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [handlePingForwarder]);

  const isOpenNotification = true;
  const [isNearBottom, setIsNearBottom] = useState(false);

  const handleScroll = () => {
    const rbMainContent = document.querySelector(".rb-main-content");

    if (!rbMainContent) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const contentOffsetTop = rbMainContent.offsetTop;
    const contentHeight = rbMainContent.offsetHeight;
    const windowHeight = window.innerHeight;
    const remainingScroll =
      contentOffsetTop + contentHeight - (scrollTop + windowHeight);

    if (
      (remainingScroll <= 80 && window.innerWidth <= 639) ||
      (remainingScroll <= 8 && window.innerWidth >= 640)
    ) {
      setIsNearBottom(true);
    } else {
      setIsNearBottom(false);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpenNotification]);

  useEffect(() => {
    const rbRoot = document.querySelector(".rb-root");
    if (currentScreen === "created" || error) {
      rbRoot.classList.remove("full-screen-min-height");
      // notify other components to refresh quota immediately
      window.dispatchEvent(new Event("quota:refresh"));
    }

    return () => {
      rbRoot.classList.remove("full-screen-min-height");
    };
  }, [currentScreen, error]);

  const landingSectionRef = useRef(null);

  //Quota used 100%
  const [noQuotaAvailable, setNoQuotaAvailable] = useState(false);
  const [quotaUsageInfo, setQuotaUsageInfo] = useState(0);
  console.log(quotaUsageInfo);
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const { quotaUsageInfo } = await fetchQuotaData(); // Use the utility function to get data
        setQuotaUsageInfo(quotaUsageInfo);

        // Show the banner if usage exceeds 90%, not on the payment page, and the banner wasn't hidden
        if (quotaUsageInfo <= 0) {
          setNoQuotaAvailable(true);
        }
      } catch (error) {
        console.log(error); // Handle any errors
      }
    };
    if (currentScreen === "created") {
      fetchQuota(); // Call the function to fetch quota data
    }
    fetchQuota(); // Call the function to fetch quota data
  }, [currentScreen]);

  //Show pop up
  const [selectPopUpShow, setSelectPopUpShow] = useState(false);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleSelectPopUpShow = useCallback((bool) => {
    setSelectPopUpShow(bool);
  }, []);

  //Link anchors verify tabs
  useEffect(() => {
    const hash = window.location.hash.substring(1); // Get the part after #
    const urlParams = new URLSearchParams(window.location.search);
    const verifyParam = urlParams.get("method");

    // Define valid verification methods
    const validMethods = ["file", "hash", "text", "portfolio"];

    // Check if the hash or query param contains a valid method
    const method = validMethods.find(
      (m) => hash.includes(m) || verifyParam === m,
    );
    if (method) {
      handleVerificationMethodChange(method);
    }
  }, []);

  return (
    <>
      <title>validityBase Stamper - Make Your Data Credible</title>
      <meta
        name="description"
        content="Manage your payment and billing information for your vBase account."
      />
      <meta
        property="og:title"
        content="validityBase Stamper - Make Your Data Credible"
      />
      <meta
        property="og:description"
        content="validityBase Stamper Application. Use stamper to quickly and easily create verifiable data provenance."
      />

      <Flex
        className={classesPage.page + " main-content"}
        direction="column"
        align={theme.flex.align.center}
      >
        {error ? (
          <StampError
            error={error}
            staticBasePath={staticBasePath}
            handleDefaultScreen={handleDefaultScreen}
          />
        ) : currentScreen === "created" ? (
          <StampCreated
            data={stampData}
            staticBasePath={staticBasePath}
            handleDefaultScreen={handleDefaultScreen}
            allHashes={allHashes}
            selectedFile={selectedFile}
            text={text}
            fileName={fileName}
            normalizedPortfolio={normalizedPortfolio}
            verificationMethod={verificationMethod}
          />
        ) : (
          <>
            <Flex
              className={classesPage.screen}
              direction="column"
              align={theme.flex.align.center}
            >
              {loading && (
                <Loading
                  title="One moment! Stamping in progress..."
                  text="Can take 20 seconds, but usually faster!"
                />
              )}

              {selectPopUpShow && (
                <SelectPopUpCollection
                  onSelectPopUpShow={handleSelectPopUpShow}
                  selectedOptionCollection={selectedOptionCollection}
                  objectHash={objectHash}
                  selectedFile={selectedFile}
                  setLoading={setLoading}
                  setError={setError}
                  handleVerifyHashWorker={handleVerifyHashWorker}
                  handleSelectedOptionCollection={
                    handleSelectedOptionCollection
                  }
                  setIsAuthenticated={setIsAuthenticated}
                  verificationMethod={verificationMethod}
                  modalOpened={selectPopUpShow}
                  setFileName={setFileName}
                  normalizedPortfolio={normalizedPortfolio}
                />
              )}

              <Box className={classesPage.header}>
                <Text className={classesPage.headerTitle}>
                  Make a{" "}
                  <Text
                    component="span"
                    className={classesPage.stampTitleBrand}
                  >
                    vBase<sup>®</sup>
                  </Text>{" "}
                  Stamp
                </Text>
                <Text className={`${classesPage.headerText}`}>
                  {verificationMethod === "portfolio"
                    ? "A tamperproof stamp of your portfolio weights"
                    : "A verifiable tamperproof timestamp for your data"}
                </Text>
                <NeedHelp
                  staticBasePath={staticBasePath}
                  linkDocs="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-stamper"
                  linkVideo="https://www.youtube.com/watch?v=wRJCNvDkKR8"
                  verificationMethod={verificationMethod}
                />
              </Box>
              <Flex direction="column">
                <Flex align={theme.flex.align.center} gap={theme.flex.gap.xs}>
                  <Flex className={buttonClasses.tabs}>
                    <CustomButton
                      id="verify-method-file"
                      text="File"
                      onClick={() => {
                        if (isAuthenticated && !noQuotaAvailable) {
                          handleVerificationMethodChange("file");
                        }
                      }}
                      componentClasses={`rb-btn-file button ${
                        verificationMethod === "file" ? "active" : ""
                      }`}
                    />

                    <CustomButton
                      id="verify-method-hash"
                      text="Content ID"
                      onClick={() => {
                        if (isAuthenticated && !noQuotaAvailable) {
                          handleVerificationMethodChange("hash");
                        }
                      }}
                      componentClasses={`rb-btn-hash button ${
                        verificationMethod === "hash" ? "active" : ""
                      }`}
                    />

                    <CustomButton
                      id="verify-method-text"
                      text="Text"
                      onClick={() => {
                        if (isAuthenticated && !noQuotaAvailable) {
                          handleVerificationMethodChange("text");
                        }
                      }}
                      componentClasses={`rb-btn-text button ${
                        verificationMethod === "text" ? "active" : ""
                      }`}
                    />

                    {!PORTFOLIO_DISABLED && (
                      <CustomButton
                        id="verify-method-portfolio"
                        text="Portfolio"
                        onClick={() => {
                          if (isAuthenticated && !noQuotaAvailable) {
                            handleVerificationMethodChange("portfolio");
                          }
                        }}
                        componentClasses={`rb-btn-portfolio button ${
                          verificationMethod === "portfolio" ? "active" : ""
                        }`}
                      />
                    )}
                  </Flex>
                  {helpIsActive && (
                    <CustomTooltip
                      content={
                        <>
                          {verificationMethod === "portfolio" ? (
                            <Text
                              classNames={{
                                root: tooltipClasses.tooltipText,
                              }}
                            >
                              Type, paste, or upload your portfolio in the
                              textbox below. For help, please see{" "}
                              <CustomAnchor
                                text="how to stamp a portfolio"
                                target="_blank"
                                componentClasses="link"
                                href="https://docs.vbase.com/use-case-how-tos/stamping-portfolios"
                              ></CustomAnchor>
                              .
                            </Text>
                          ) : (
                            <>
                              <Text
                                classNames={{
                                  root: tooltipClasses.tooltipText,
                                }}
                              >
                                Choose whether to calculate a digital
                                fingerprint from a local file, or input a
                                digital fingerprint/content ID directly
                                (advanced).
                              </Text>
                              <Text
                                classNames={{
                                  root: tooltipClasses.tooltipText,
                                }}
                              >
                                <CustomAnchor
                                  text="Learn more"
                                  target="_blank"
                                  componentClasses="link"
                                  href="https://docs.vbase.com/web-tools/how-to-use-vbase-stamper"
                                ></CustomAnchor>
                              </Text>
                            </>
                          )}
                        </>
                      }
                    ></CustomTooltip>
                  )}
                </Flex>
              </Flex>

              {verificationMethod === "file" && (
                <StampFromFile
                  currentScreen={currentScreen}
                  staticBasePath={staticBasePath}
                  objectHash={objectHash}
                  selectedFileName={selectedFile?.name ?? ""}
                  handleFileInput={handleFileInput}
                  isAuthenticated={isAuthenticated}
                  isDisabled={isDisabled}
                  helpIsActive={helpIsActive}
                  noQuotaAvailable={noQuotaAvailable}
                  onSelectedOptionCollection={handleSelectedOptionCollection}
                  selectedOptionCollection={selectedOptionCollection}
                  activateSelectCollection={activateSelectCollection}
                  onActivateSelectCollection={handleActivateSelectCollection}
                  selectCollections={selectCollections}
                  filePortfolioValidation={filePortfolioValidation}
                />
              )}

              {verificationMethod === "hash" && (
                <StampContentId
                  currentScreen={currentScreen}
                  staticBasePath={staticBasePath}
                  isValid={isValid}
                  objectHash={objectHash}
                  handleHashChange={handleHashChange}
                  handleRemoveHash={handleRemoveHash}
                  handleCreateSubmit={handleCreateSubmit}
                  isSmallScreen={isSmallScreen}
                  isAuthenticated={isAuthenticated}
                  buttonStampClassName={buttonStampClassName}
                  noQuotaAvailable={noQuotaAvailable}
                  helpIsActive={helpIsActive}
                  onSelectedOptionCollection={handleSelectedOptionCollection}
                  selectedOptionCollection={selectedOptionCollection}
                  activateSelectCollection={activateSelectCollection}
                  onActivateSelectCollection={handleActivateSelectCollection}
                  selectCollections={selectCollections}
                />
              )}

              {verificationMethod === "text" && (
                <StampFromText
                  currentScreen={currentScreen}
                  staticBasePath={staticBasePath}
                  text={text}
                  setText={handleSetText}
                  handleTextToFile={handleTextToFile}
                  handleRemoveText={handleRemoveText}
                  handleCreateSubmit={handleCreateSubmit}
                  isDisabled={isDisabled}
                  isSmallScreen={isSmallScreen}
                  isAuthenticated={isAuthenticated}
                  button={button}
                  buttonStampClassName={buttonStampClassName}
                  noQuotaAvailable={noQuotaAvailable}
                  helpIsActive={helpIsActive}
                  onSelectedOptionCollection={handleSelectedOptionCollection}
                  selectedOptionCollection={selectedOptionCollection}
                  activateSelectCollection={activateSelectCollection}
                  onActivateSelectCollection={handleActivateSelectCollection}
                  selectCollections={selectCollections}
                  textPortfolioValidation={textPortfolioValidation}
                />
              )}

              {!PORTFOLIO_DISABLED && verificationMethod === "portfolio" && (
                <StampFromPortfolio
                  currentScreen={currentScreen}
                  staticBasePath={staticBasePath}
                  portfolioText={portfolioText}
                  setPortfolioText={handleSetPortfolioText}
                  handlePortfolioToFile={handlePortfolioToFile}
                  isSmallScreen={isSmallScreen}
                  isAuthenticated={isAuthenticated}
                  button={button}
                  buttonStampClassName={buttonStampClassName}
                  noQuotaAvailable={noQuotaAvailable}
                  helpIsActive={helpIsActive}
                  onSelectedOptionCollection={handleSelectedOptionCollection}
                  selectedOptionCollection={selectedOptionCollection}
                  activateSelectCollection={activateSelectCollection}
                  onActivateSelectCollection={handleActivateSelectCollection}
                  selectCollections={selectCollections}
                  portfolioValidation={portfolioValidation}
                  setPortfolioValidation={setPortfolioValidation}
                />
              )}

              {(!isSmallScreen || verificationMethod === "file") && (
                <Flex
                  gap={theme.flex.gap.xs}
                  align={theme.flex.align.center}
                  className={GeneralClasses.stampInfo}
                >
                  <Tooltip
                    label={portfolioTooltipText}
                    disabled={!portfolioTooltipText}
                    multiline
                    w={300}
                    withArrow
                    position="top"
                    transitionProps={{ transition: "fade", duration: 200 }}
                  >
                    <Box component="span">
                      <CustomButton
                        text="Make a vBase Stamp!"
                        componentClasses={buttonStampClassName}
                        onClick={handleCreateSubmit}
                        disabled={isButtonDisabled()}
                      />
                    </Box>
                  </Tooltip>

                  {!isAuthenticated && (
                    <Box className={GeneralClasses.stampHashMobileLogin}>
                      <CustomAnchor
                        text="Sign In"
                        href="/accounts/login/"
                        componentClasses="link"
                      />
                      <Text component="span"> to Start Stamping!</Text>
                    </Box>
                  )}
                  {helpIsActive && (
                    <CustomTooltip
                      content={
                        <>
                          <Text
                            classNames={{
                              root: tooltipClasses.tooltipText,
                            }}
                          >
                            Once you click “Make a vBase Stamp” the digital
                            fingerprint of your data will be included in a
                            blockchain transaction, creating a trusted
                            verifiable timestamp!
                          </Text>
                          <Text
                            classNames={{
                              root: tooltipClasses.tooltipText,
                            }}
                          >
                            <CustomAnchor
                              text="Learn more"
                              target="_blank"
                              componentClasses="link"
                              href="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-stamper"
                            ></CustomAnchor>
                          </Text>
                        </>
                      }
                    ></CustomTooltip>
                  )}
                </Flex>
              )}
              <Box className={GeneralClasses.stampBulkText}>
                Need to stamp in bulk?{" "}
                <CustomAnchor
                  text="Check out our handy API"
                  href="https://docs.vbase.com/getting-started/python-quickstart"
                  target="_blank"
                  componentClasses="link"
                />
              </Box>
            </Flex>
            <TriggerHelp
              helpIsActive={helpIsActive}
              isNearBottom={isNearBottom}
              handleHelpTriggerClick={handleHelpTriggerClick}
            />
          </>
        )}
        <LearnMore
          staticBasePath={staticBasePath}
          landingSectionRef={landingSectionRef}
        />
      </Flex>
      <Landing
        staticBasePath={staticBasePath}
        isAuthenticated={isAuthenticated}
        landingSectionRef={landingSectionRef}
      />

      {loading && currentScreen !== "created" && (
        <Box className={loaderClasses.bg}></Box>
      )}
    </>
  );
};

StampForm.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  djangoContext: PropTypes.object.isRequired,
  setAuthenticated: PropTypes.func,
  event: PropTypes.object,
  setIsOpenNotification: PropTypes.func,
  quotaUsageInfo: PropTypes.object,
};

export default StampForm;
